import { format, isValid } from 'date-fns';
import { de } from 'date-fns/locale';

/**
 * Safely formats a date string, with comprehensive validation
 * Returns a fallback string if the date is invalid
 *
 * @param dateString - The date string to format
 * @param fallbackMessage - The message to return if date is invalid
 * @returns Formatted date string or fallback message
 */
export const safeFormatDate = (dateString: string | undefined | null, fallbackMessage: string): string => {
  if (!dateString) return fallbackMessage;

  try {
    const date = new Date(dateString);
    return isValid(date) ? format(date, 'd. MMMM y', { locale: de }) : fallbackMessage;
  } catch (error) {
    // Silent failure with fallback
    return fallbackMessage;
  }
};

/**
 * Safely formats a timestamp, handling both millisecond and second formats
 * Returns a fallback string if the timestamp is invalid
 *
 * @param timestamp - The timestamp to format (can be in seconds or milliseconds)
 * @param fallbackMessage - The message to return if timestamp is invalid
 * @returns Formatted date string or fallback message
 */
export const safeFormatTimestamp = (timestamp: number | undefined | null, fallbackMessage: string): string => {
  if (!timestamp || timestamp <= 0) return fallbackMessage;

  try {
    // Convert to milliseconds if needed (timestamp in seconds)
    const timestampMs = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
    const date = new Date(timestampMs);
    return isValid(date) ? format(date, 'd. MMMM y', { locale: de }) : fallbackMessage;
  } catch (error) {
    // Silent failure with fallback
    return fallbackMessage;
  }
};
